import React, { useState, useEffect } from "react";
// import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import QRCodeDisplay from "@/components/ui/qr-code-display";
import { toast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import { purchaseListService } from "@/services";
import { AvailableProduct } from "@/types/purchaseList.types";
import {
  QrCode,
  Package,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  Download,
  ShoppingCart,
} from "lucide-react";

interface StudentQRViewProps {
  className?: string;
}

const StudentQRView: React.FC<StudentQRViewProps> = ({ className }) => {
  const { user } = useAuth();
  const [availableProducts, setAvailableProducts] = useState<
    AvailableProduct[]
  >([]);
  const [selectedProducts, setSelectedProducts] = useState<AvailableProduct[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedQR, setGeneratedQR] = useState<string | null>(null);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);

  const loadStudentProducts = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await purchaseListService.getStudentAvailableProducts(
        user.id
      );
      setAvailableProducts(response.availableProducts || []);
      setSelectedProducts([]); // Reset selections
      setGeneratedQR(null); // Reset QR
    } catch (error: any) {
      console.error("Error loading products:", error);
      setAvailableProducts([]);
      setError("Error al cargar los productos disponibles");
      toast({
        title: "Error",
        description: "No se pudieron cargar los productos disponibles",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar productos al montar el componente
  useEffect(() => {
    loadStudentProducts();
  }, [user]);

  // Manejar selección/deselección de productos
  const handleProductSelection = (
    product: AvailableProduct,
    checked: boolean
  ) => {
    if (checked) {
      setSelectedProducts((prev) => [...prev, product]);
    } else {
      setSelectedProducts((prev) =>
        prev.filter((p) => p.assignmentId !== product.assignmentId)
      );
    }
  };

  // Generar QR para productos seleccionados
  const handleGenerateQR = async () => {
    if (selectedProducts.length === 0) {
      toast({
        title: "Selección requerida",
        description:
          "Debes seleccionar al menos un producto para generar el QR",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingQR(true);
    try {
      const qrData = {
        studentId: user?.id || "",
        studentName: (user?.nombre || "") + " " + (user?.apellido || ""),
        products: selectedProducts.map((product) => ({
          assignmentId: product.assignmentId,
          productId: product.productId,
          productName: product.productName,
          quantity: 1, // Por ahora 1 por producto, se puede hacer configurable
        })),
      };

      const response = await purchaseListService.generateQRForMultipleProducts(
        qrData
      );
      setGeneratedQR(response.qrCode);

      toast({
        title: "QR Generado",
        description: `QR generado para ${response.totalProducts} producto(s)`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo generar el QR",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingQR(false);
    }
  };

  // Obtener estado del producto
  const getProductStatus = (product: AvailableProduct) => {
    if (product.remainingQuantity === 0) {
      return { status: "Agotado", variant: "secondary" as const };
    } else if (product.remainingQuantity < product.assignedQuantity * 0.3) {
      return { status: "Pocas unidades", variant: "destructive" as const };
    } else {
      return { status: "Disponible", variant: "default" as const };
    }
  };

  // Verificar si un producto está seleccionado
  const isProductSelected = (product: AvailableProduct) => {
    return selectedProducts.some(
      (p) => p.assignmentId === product.assignmentId
    );
  };

  // Descargar QR como imagen
  const handleDownloadQR = async () => {
    if (!generatedQR) return;

    try {
      const canvas = document.querySelector("canvas");
      if (canvas) {
        const link = document.createElement("a");
        link.download = `qr-redemption-${Date.now()}.png`;
        link.href = canvas.toDataURL();
        link.click();

        toast({
          title: "QR Descargado",
          description: "El código QR se ha descargado correctamente",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo descargar el QR",
        variant: "destructive",
      });
    }
  };

  return (
    <div className={`space-y-4 sm:space-y-6 ${className || ""}`}>
      {/* Estado de carga */}
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="flex items-center gap-3">
            <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary" />
            <span className="text-sm sm:text-base text-muted-foreground">
              Cargando productos...
            </span>
          </div>
        </div>
      )}

      {/* Estado de error */}
      {error && (
        <Alert variant="destructive" className="rounded-2xl border-destructive/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-destructive-foreground">
            {error}
            <Button
              variant="outline"
              size="sm"
              onClick={loadStudentProducts}
              className="mt-2 h-8 rounded-xl"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Lista de productos disponibles */}
      {!isLoading && !error && (
        <>
          {availableProducts.length === 0 ? (
            <div className="text-center p-8">
              <div className="relative inline-block mb-4">
                <div className="absolute inset-0 bg-gradient-to-r from-muted to-muted/60 rounded-full blur-xl opacity-50"></div>
                <Package className="h-12 w-12 text-muted-foreground relative z-10 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No hay productos disponibles
              </h3>
              <p className="text-sm text-muted-foreground">
                No tienes productos asignados para canjear en este momento.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
                  Mis Productos Disponibles
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadStudentProducts}
                  className="h-8 rounded-xl hover:scale-105 transition-all duration-200 hover:shadow-lg"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Actualizar</span>
                  <span className="sm:hidden">Refrescar</span>
                </Button>
              </div>

              <div className="space-y-3">
                {availableProducts.map((product) => (
                  <div
                    key={product.assignmentId}
                    className="group p-4 rounded-2xl bg-gradient-to-br from-card/50 to-card/30 border border-border/50 hover:border-primary/30 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <Checkbox
                        checked={isProductSelected(product)}
                        onCheckedChange={(checked) =>
                          handleProductSelection(product, checked as boolean)
                        }
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm sm:text-base font-medium text-foreground truncate">
                              {product.productName}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge
                                variant="secondary"
                                className="text-xs shrink-0"
                              >
                                Cantidad: {product.remainingQuantity}
                              </Badge>
                              <Badge
                                variant={getProductStatus(product).variant}
                                className="text-xs shrink-0"
                              >
                                {getProductStatus(product).status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Botón para generar QR */}
              <div className="pt-4">
                <Button
                  onClick={handleGenerateQR}
                  disabled={selectedProducts.length === 0 || isGeneratingQR}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGeneratingQR ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generando QR...
                    </>
                  ) : (
                    <>
                      <QrCode className="h-4 w-4 mr-2" />
                      Generar Código QR
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* QR Generado */}
      {generatedQR && (
        <div className="mt-6 p-6 rounded-2xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-green-600">
                ¡QR Generado Exitosamente!
              </h3>
            </div>
            
            <div className="flex justify-center">
              <div className="p-4 bg-white rounded-2xl shadow-lg">
                <QRCodeDisplay
                  value={generatedQR}
                  size={200}
                  className="rounded-xl"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={handleDownloadQR}
                variant="outline"
                className="rounded-xl hover:scale-105 transition-all duration-200 hover:shadow-lg"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar QR
              </Button>
              <Button
                onClick={() => {
                  setGeneratedQR(null);
                  setSelectedProducts([]);
                }}
                variant="outline"
                className="rounded-xl hover:scale-105 transition-all duration-200 hover:shadow-lg"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Generar Nuevo QR
              </Button>
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p className="mb-2">
                <strong>Productos seleccionados:</strong>
              </p>
              <ul className="space-y-1">
                {selectedProducts.map((product) => (
                  <li key={product.assignmentId} className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    {product.productName} (Cantidad: {product.remainingQuantity})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentQRView;
